import React from 'react';
import { Route, Gauge, Car } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров маршрута
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating,
  vehicleTypes
}) => {
  // Расчет текущего расстояния
  const distance = parseFloat(formData.distance) || 0;
  
  // Расчет текущей скорости
  const averageSpeed = parseFloat(formData.averageSpeed) || 0;
  
  // Обработчик изменения расстояния
  const handleDistanceChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      distance: newValue
    }));
  };
  
  // Обработчик изменения средней скорости
  const handleSpeedChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      averageSpeed: newValue
    }));
  };
  
  // Предопределенные расстояния для быстрого выбора (популярные маршруты в России)
  const predefinedRoutes = [
    { title: 'Москва-СПб', distance: '700', speed: '90' },
    { title: 'Москва-Казань', distance: '815', speed: '85' },
    { title: 'СПб-Псков', distance: '290', speed: '80' },
    { title: 'Краснодар-Сочи', distance: '280', speed: '75' }
  ];
  
  // Предопределенные скорости для быстрого выбора
  const predefinedSpeeds = [
    { value: '60', label: '60 км/ч' },
    { value: '80', label: '80 км/ч' },
    { value: '90', label: '90 км/ч' },
    { value: '110', label: '110 км/ч' }
  ];
  
  // Функция расчета
  const calculateRoute = () => {
    // Здесь будет вызвана функция основного расчета
    // В данном случае просто эмулируем действие
    document.querySelector('button[type="submit"]')?.click();
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Gauge className="w-5 h-5 mr-2 text-blue-500" />
        Быстрая настройка
      </h3>
      
      <div className="space-y-5">
        {/* Расстояние */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="distanceSlider" className="text-gray-600 dark:text-gray-400">Расстояние</label>
            <span className="font-medium text-gray-900 dark:text-white">{formatNumber(distance, 0)} км</span>
          </div>
          <input
            type="range"
            id="distanceSlider"
            min="10"
            max="2000"
            step="10"
            value={distance}
            onChange={handleDistanceChange}
            aria-label="Расстояние в километрах"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
          />
          
          {/* Кнопки с популярными маршрутами */}
          <div className="text-xs text-gray-600 dark:text-gray-400 mt-2 mb-1">Популярные маршруты в России:</div>
          <div className="grid grid-cols-2 gap-1 mt-1">
            {predefinedRoutes.map(route => (
              <button
                key={route.title}
                type="button"
                onClick={() => setFormData(prev => ({ 
                  ...prev, 
                  distance: route.distance,
                  averageSpeed: route.speed
                }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.distance === route.distance 
                    ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {route.title}
              </button>
            ))}
          </div>
        </div>
        
        {/* Средняя скорость */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="speedSlider" className="text-gray-600 dark:text-gray-400">Средняя скорость</label>
            <span className="font-medium text-gray-900 dark:text-white">
              {formatNumber(averageSpeed, 0)} км/ч
            </span>
          </div>
          <input
            type="range"
            id="speedSlider"
            min="30"
            max="150"
            step="5"
            value={averageSpeed}
            onChange={handleSpeedChange}
            aria-label="Средняя скорость (км/ч)"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
          />
          
          {/* Кнопки с предопределенными скоростями */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedSpeeds.map(speed => (
              <button
                key={speed.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, averageSpeed: speed.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.averageSpeed === speed.value 
                    ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {speed.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Выбор типа транспорта */}
        <div>
          <div className="flex justify-between items-center mb-2 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Тип транспорта</label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {vehicleTypes.slice(0, 4).map(type => (
              <button
                key={type.id}
                type="button"
                onClick={() => setFormData(prev => ({ 
                  ...prev, 
                  vehicleType: type.id,
                  averageSpeed: type.defaultSpeed.toString()
                }))}
                className={`p-2 text-sm rounded-lg border transition-colors flex flex-col items-center
                  ${formData.vehicleType === type.id 
                    ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                <Car className="w-4 h-4 mb-1" />
                <div className="font-medium">{type.name}</div>
              </button>
            ))}
          </div>
        </div>
        
        {/* Переключатель перерывов */}
        <div className="flex items-center justify-between py-2 border-t border-gray-200 dark:border-gray-700">
          <span className="text-sm text-gray-700 dark:text-gray-300">Учитывать перерывы</span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input 
              type="checkbox" 
              className="sr-only peer" 
              checked={formData.breaks}
              onChange={(e) => setFormData(prev => ({ ...prev, breaks: e.target.checked }))}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
          </label>
        </div>
        
        {/* Переключатель платных дорог */}
        <div className="flex items-center justify-between py-2 border-t border-gray-200 dark:border-gray-700">
          <span className="text-sm text-gray-700 dark:text-gray-300">Платные дороги</span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input 
              type="checkbox" 
              className="sr-only peer" 
              checked={formData.tollRoads}
              onChange={(e) => setFormData(prev => ({ ...prev, tollRoads: e.target.checked }))}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
          </label>
        </div>
        
        {/* Кнопка расчета */}
        <Button
          primary
          fullWidth
          onClick={calculateRoute}
          icon={<Route className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать маршрут
        </Button>
        
        {/* Ожидаемое время в пути */}
        {distance > 0 && averageSpeed > 0 && (
          <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg text-sm text-blue-700 dark:text-blue-300">
            <div className="font-medium mb-1">Примерное время в пути:</div>
            <div className="text-lg font-bold">
              {formatTime(distance / averageSpeed)}
            </div>
            <div className="text-xs mt-1">Без учета остановок и дорожных условий</div>
          </div>
        )}
      </div>
    </div>
  );
};

// Вспомогательная функция для форматирования времени
const formatTime = (hours) => {
  const wholeHours = Math.floor(hours);
  const minutes = Math.round((hours - wholeHours) * 60);
  
  if (wholeHours === 0) {
    return `${minutes} мин`;
  } else if (minutes === 0) {
    return `${wholeHours} ч`;
  } else {
    return `${wholeHours} ч ${minutes} мин`;
  }
};

export default QuickAdjustmentSidebar;